"use client";
import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

export default function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setStatus("loading");
    setMessage("");

    try {
      if (!email || !email.includes("@")) {
        throw new Error("Please enter a valid email address");
      }

      const apiUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:5000";
      const endpoint = `${apiUrl}/api/newsletter/subscribe`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server configuration error. Please try again later.");
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to subscribe");
      }

      setStatus("success");
      setMessage(result.message || "Thank you for subscribing!");
      setEmail("");

      setTimeout(() => {
        setStatus("idle");
        setMessage("");
      }, 5000);

    } catch (error) {
      console.error("Newsletter subscription error:", error);
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Failed to subscribe. Please try again.");

      setTimeout(() => {
        setStatus("idle");
        setMessage("");
      }, 5000);
    }
  };

  return (
    <>
        {message && (
            <div
              className={`w-full max-w-[700px] p-4 rounded-lg flex items-center gap-3 ${
                status === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              {status === "success" && <CheckCircle2 className="w-5 h-5" />}
              <p className="text-sm font-medium">{message}</p>
            </div>
        )}

        <div className="flex flex-col sm:flex-row relative w-full max-w-[700px]">
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email..."
              disabled={status === "loading"}
              className="w-full py-4 text-black rounded-xl sm:rounded-r-none pl-5 bg-white border border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-transparent"
            />

            <button
              onClick={handleSubmit}
              disabled={status === "loading"}
              className="flex items-center justify-center gap-3 px-5 py-4 rounded-xl sm:rounded-l-none sm:absolute sm:right-0 mt-3 sm:mt-0 bg-secondary hover:bg-secondary text-black text-[15px] sm:text-[16px] sm:w-[180px] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Subscribing...
                </>
              ) : (
                <>
                  Subscribe <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
        </div>
    </>    
  );
}